import { useState, useEffect } from 'react';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Onboarding from './pages/Onboarding';
import FeedbackWidget, { FeedbackPill } from './components/FeedbackWidget';

export default function App() {
  const [authed, setAuthed] = useState(null);
  const [onboarded, setOnboarded] = useState(null);
  const [userName, setUserName] = useState('');
  // Which unauthenticated screen to show: 'landing' (default public front door), 'login', or
  // 'signup'. No router — this mirrors the existing conditional-render auth gate.
  const [authView, setAuthView] = useState('landing');

  // Chat state lifted to App — survives view changes
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  // Anxiety-reduction "hand this over" card — open-state lives here with the rest of chat
  // state (the card renders inline in the chat thread). `anxietyPrefill` seeds the first
  // field when opened via the EnergyBanner mic (dictate-first path).
  const [anxietyCardOpen, setAnxietyCardOpen] = useState(false);
  const [anxietyPrefill, setAnxietyPrefill] = useState('');

  // Feedback panel open-state, lifted here so the trigger (in the header) and the panel
  // (an overlay) stay connected without globals.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const openFeedback = () => setFeedbackOpen(true);

  async function checkAuthAndRestore() {
    try {
      const r = await fetch('/api/state');
      if (!r.ok) { setAuthed(false); setOnboarded(null); return; }
      const stateData = await r.json();
      if (stateData.userName) setUserName(stateData.userName);
      // Default true if field missing — avoids locking out users on old deploys
      setOnboarded(stateData.onboarded !== undefined ? !!stateData.onboarded : true);
      try {
        const convR = await fetch('/api/session/conversation');
        if (convR.ok) {
          const { messages, sessionId } = await convR.json();
          if (messages.length > 0) {
            setChatMessages(messages);
            setChatSessionId(sessionId);
          }
        }
      } catch (_) {}
      setAuthed(true);
    } catch {
      setAuthed(false);
      setOnboarded(null);
    }
  }

  async function handleLogout() {
    await fetch('/api/logout', { method: 'POST' });
    window.location.reload();
  }

  useEffect(() => { checkAuthAndRestore(); }, []); // eslint-disable-line

  const feedbackPanel = (
    <FeedbackWidget open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
  );

  if (authed === null) return <Loader />;
  if (!authed) {
    if (authView === 'signup') {
      return <Signup onAuthed={checkAuthAndRestore} onShowLogin={() => setAuthView('login')} />;
    }
    if (authView === 'login') {
      return <Login onLogin={checkAuthAndRestore} onShowSignup={() => setAuthView('signup')} />;
    }
    return <Landing onSignIn={() => setAuthView('login')} />;
  }
  if (onboarded === null) return <Loader />;
  if (!onboarded) {
    // Onboarding renders each step via early returns with no shared header, so the pill
    // is placed here (fixed, top-right) to stay present across all of its screens.
    return (
      <>
        <Onboarding onComplete={checkAuthAndRestore} onLogout={handleLogout} />
        <FeedbackPill onClick={openFeedback} className="fb-pill-fixed" />
        {feedbackPanel}
      </>
    );
  }

  const chatProps = {
    chatMessages, setChatMessages,
    chatSessionId, setChatSessionId,
    pendingAction, setPendingAction,
    anxietyCardOpen, setAnxietyCardOpen,
    anxietyPrefill, setAnxietyPrefill,
    handleLogout,
    userName,
    openFeedback,
  };

  return (
    <>
      <Dashboard chatProps={chatProps} />
      {feedbackPanel}
    </>
  );
}

function Loader() {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--text-3)',
    }}>Loading…</div>
  );
}
